import { ZoomOut, ZoomIn, Crop, FileUploadOutlined } from '@mui/icons-material';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Slider, Stack, IconButton, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

const TARGET_WIDTH = 1240;
const TARGET_HEIGHT = 620;
const ASPECT = TARGET_WIDTH / TARGET_HEIGHT;

const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
};

const getCroppedPng = async (imageSrc: string, pixelCrop: Area): Promise<Blob | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = TARGET_WIDTH;
  canvas.height = TARGET_HEIGHT;

  if (ctx) {
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
};

type FundraiserMotifCropperModalProps = {
  open: boolean;
  onComplete: (file: File) => void;
  onAbort: () => void;
};

export const FundraiserMotifCropperModal = ({ open, onComplete, onAbort }: FundraiserMotifCropperModalProps) => {
  const { t } = useTranslation(['fundraiser', 'common']);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const minZoom = 1;
  const maxZoom = 3;

  const allowedTypes = ['image/png', 'image/jpeg'];

  const handleZoom = (val: number) => {
    if (val > 0) {
      setZoom(Math.min(maxZoom, zoom + 0.1));
    } else {
      setZoom(Math.max(minZoom, zoom - 0.1));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      setError(t('fundraiser:giftCertificate.cropMotifInvalidFormat'));
      e.target.value = '';
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleConfirm = async () => {
    if (!croppedAreaPixels || !image) return;
    setProcessing(true);
    try {
      const blob = await getCroppedPng(image, croppedAreaPixels);
      if (blob) {
        const file = new File([blob], 'background.png', { type: 'image/png' });
        onComplete(file);
      }
    } catch (err) {
      ConsoleHelper.log(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setImage(null);
    setError('');
    onAbort();
  };

  return (
    <Dialog open={open} maxWidth='md' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Crop fontSize='small' sx={{ mr: 1 }} /> {t('fundraiser:giftCertificate.cropMotifTitle')}
      </DialogTitle>
      <DialogContent>
        <Alert severity='info' sx={{ mb: 2 }}>
          {t('fundraiser:giftCertificate.cropMotifInfo')}
        </Alert>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {t('fundraiser:giftCertificate.cropMotifRequirements')}
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <input ref={fileInputRef} type='file' accept='image/png,image/jpeg' hidden onChange={handleFileSelect} />

        {!image && (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <FileUploadOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color='text.secondary'>{t('fundraiser:giftCertificate.cropMotifSelectFile')}</Typography>
            <Typography variant='caption' color='text.disabled'>
              PNG / JPG &middot; {TARGET_WIDTH} x {TARGET_HEIGHT} px
            </Typography>
          </Box>
        )}

        {image && (
          <>
            <Box sx={{ position: 'relative', minHeight: '22rem' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: '80px' }}>
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={ASPECT}
                  onCropChange={setCrop}
                  onCropComplete={(_, area) => setCroppedAreaPixels(area)}
                  onZoomChange={setZoom}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  width: '50%',
                  transform: 'translateX(-50%)',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Stack spacing={2} direction='row' sx={{ mb: 1, width: '100%' }} alignItems='center'>
                  <IconButton onClick={() => handleZoom(-1)} disabled={zoom === minZoom} size='large'>
                    <ZoomOut sx={{ color: (thm) => thm.palette.grey[zoom === minZoom ? 200 : 700] }} />
                  </IconButton>
                  <Slider value={zoom} min={minZoom} max={maxZoom} step={0.1} size='small' onChange={(_, zoomValue) => setZoom(Number(zoomValue))} />
                  <IconButton onClick={() => handleZoom(1)} disabled={zoom === maxZoom} size='large'>
                    <ZoomIn sx={{ color: (thm) => thm.palette.grey[zoom === maxZoom ? 200 : 700] }} />
                  </IconButton>
                </Stack>
              </Box>
            </Box>
            <Button
              variant='outlined'
              size='small'
              onClick={() => {
                setImage(null);
                setCroppedAreaPixels(null);
              }}
              sx={{ mt: 1 }}
            >
              {t('fundraiser:giftCertificate.cropBackgroundChangeFile')}
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color='primary' variant='outlined' onClick={handleClose} disabled={processing}>
          {t('common:button.cancel')}
        </Button>
        <Button color='primary' variant='contained' disableElevation onClick={handleConfirm} disabled={processing || !croppedAreaPixels}>
          {t('common:button.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
